import type { UiResources } from './types.ts';

/**
 * UI copy, Spanish (D9 skeleton): the field app only (the panel is EN/TR), so `common` and
 * `field`. Latin American Spanish, formal "usted", as in the SMS copy.
 */
export const uiEs: UiResources = {
  common: {
    appName: 'TalkOlog',
    language: 'Idioma',
    languageNames: { en: 'English', es: 'Español', tr: 'Türkçe' },
    loading: 'Cargando…',
    retry: 'Intente de nuevo',
    close: 'Cerrar',
    errorTitle: 'Algo salió mal',
  },
  field: {
    routes: {
      link: 'Abriendo su enlace…',
      today: 'Hoy',
      record: 'Grabar',
      report: 'Reporte',
      history: 'Reportes anteriores',
      settings: 'Configuración',
      expired: 'Enlace vencido',
      talk: 'Hable ahora',
      photos: 'Agregar fotos',
      legal: 'Texto legal',
    },
    legal: {
      version: 'Versión {{version}} · {{date}}',
      otherLanguage: 'Este texto aún no está disponible en su idioma.',
      unknown: {
        title: 'Texto no encontrado',
        body: 'Este texto no está disponible aquí. Pida a la oficina el enlace más reciente.',
      },
      back: 'Volver',
    },
    notFound: {
      title: 'Página no encontrada',
      body: 'Esa página no existe o el enlace está mal escrito.',
      action: 'Ir a Hoy',
    },
    link: {
      notFound: {
        title: 'Enlace no encontrado',
        body: 'Este enlace no funciona. Pida a la oficina que le envíe uno nuevo.',
      },
      revoked: {
        title: 'Enlace ya no válido',
        body: 'Este enlace fue desactivado. Pida a la oficina que le envíe uno nuevo.',
      },
      disabled: {
        title: 'Cuenta en pausa',
        body: 'Sus reportes están en pausa. Consulte a la oficina si cree que es un error.',
      },
      none: {
        title: 'Abra su enlace',
        body: 'Abra TalkOlog desde el enlace de su mensaje de texto.',
      },
      failed: 'No se pudo abrir su enlace. Revise su señal e intente de nuevo.',
    },
    device: {
      title: '¿Este es el teléfono de {{name}}?',
      body: 'Enviamos un código de 6 dígitos por SMS o WhatsApp al número que nos dio la oficina, para que solo usted pueda reportar desde aquí.',
      send: 'Sí, envíenme un código',
      sending: 'Enviando el código…',
      sent: 'Código enviado. Vence en 10 minutos.',
      codeLabel: 'Código de 6 dígitos',
      verify: 'Continuar',
      verifying: 'Verificando…',
      resend: 'Enviar un código nuevo',
      resendIn: 'Puede pedir otro código en {{minutes}} min',
      errors: {
        code_invalid: 'Ese código no es correcto. Intentos restantes: {{attemptsLeft}}.',
        code_expired: 'Ese código venció. Pida uno nuevo.',
        no_code: 'Primero pida un código.',
        code_undeliverable:
          'No pudimos enviar un SMS ni un WhatsApp a este número. Pida a la oficina que lo revise.',
        rate_limited: 'Demasiados intentos. Intente de nuevo en {{minutes}} min.',
        offline: 'Sin conexión. Revise su señal e intente de nuevo.',
        generic: 'Algo salió mal. Intente de nuevo.',
      },
    },
    welcome: {
      title: 'Hola {{name}} — un minuto antes de su primer reporte',
      notice:
        'Sus notas de voz, fotos y llamadas se graban para escribir el reporte diario de {{company}}. La oficina las ve; las grabaciones se guardan 2 años.',
      privacy: 'Aviso de privacidad',
      consentRequired: 'Acepto la grabación y el procesamiento de mis reportes',
      consentSms: 'Envíenme por texto el enlace del reporte diario',
      consentCall: 'Llámenme a la hora del reporte (llamada automática)',
      consentWhatsapp: 'Envíenme mensajes por WhatsApp',
      whatsappNotice:
        'Al continuar, acepta recibir por WhatsApp sus enlaces de reporte diario y recordatorios. Responda STOP en cualquier momento para dejar de recibirlos.',
      prefLegend: '¿Cómo quiere reportar?',
      prefLink: 'Enlace por texto',
      prefLinkHint: 'Toque el enlace, hable, listo. Agregue fotos.',
      prefCall: 'Llamada telefónica',
      prefCallHint: 'TalkOlog le llama y escucha.',
      prefNote: 'Siempre puede usar la otra opción.',
      continue: 'Continuar',
      saving: 'Guardando…',
      requiredHint: 'Marque la primera casilla para continuar.',
      failed: 'No se pudo guardar. Revise su señal e intente de nuevo.',
    },
    welcomeTest: {
      title: 'Haga una prueba de 20 segundos',
      body: 'Diga cualquier cosa — no se archiva nada.',
      start: 'Empezar la prueba',
      recording: 'Grabando',
      stop: 'Detener',
      checking: 'Escuchando…',
      heard: 'Le escuché bien: “{{text}}”',
      silent: 'No escuché nada. Revise el micrófono e intente de nuevo.',
      failed: 'No se pudo revisar la grabación. Puede seguir igual.',
      tooMany: 'Ya son suficientes pruebas por ahora. Puede seguir.',
      noMic:
        'El micrófono está apagado. Permítalo en los ajustes del teléfono para grabar sus reportes.',
      again: 'Intentar de nuevo',
      skip: 'Saltar la prueba',
      continue: 'Ir a hoy',
    },
    today: {
      due: 'VENCE {{time}}',
      changeSite: 'Cambiar de obra',
      siteSheetTitle: '¿Qué obra?',
      status: {
        notSent: 'Sin enviar',
        inProgress: 'En proceso',
        sent: 'Enviado',
        weatherDay: 'Día de clima',
        noWork: 'Hoy sin trabajo',
      },
      promptsTitle: 'Dígalo en cualquier orden',
      prompts: {
        work: 'Trabajo hecho y dónde',
        crew: 'Cuadrilla por empresa',
        hours: 'Horas',
        weather: 'Clima y tiempo perdido',
        deliveries: 'Entregas',
        safety: 'Seguridad',
      },
      record: 'Grabar el reporte de hoy',
      tapToRecord: 'Toque para grabar',
      usually: 'Normalmente 2–3 minutos',
      addPhotos: 'Agregar fotos',
      noWork: 'Hoy sin trabajo',
      weather: 'Obra cerrada: clima',
      talkNow: '¿Prefiere hablar? Hable ahora',
      talkNowHint: 'El asistente escucha en el navegador, sin número',
      call: 'o llame al {{number}}',
      dateQuestion: '¿Es para hoy o para ayer?',
      dateToday: 'Hoy',
      dateYesterday: 'Ayer, {{date}}',
      closed: 'El reporte de este día ya está. Desde aquí no se puede agregar más.',
      weatherPhoto: 'Agregar una foto de las condiciones',
      weatherPhotoHint: 'Una foto de las condiciones refuerza el registro del retraso.',
      daySheet: {
        noWorkTitle: '¿Hoy sin trabajo?',
        noWorkBody: 'La oficina verá “Hoy sin trabajo” para {{site}}.',
        weatherTitle: '¿Obra cerrada por el clima?',
        weatherBody: 'La oficina verá “Día de clima” para {{site}}.',
        since: '¿Desde cuándo?',
        never: 'Nunca empezó',
        sinceHint: 'Elige desde cuándo para confirmar.',
        workersBefore: '¿Cuántos había en la obra antes?',
        confirm: 'Confirmar',
        cancel: 'Cancelar',
        saving: 'Guardando…',
        errors: {
          report_in_progress: 'Ya hay un reporte en camino para hoy. Termínelo primero.',
          report_filed: 'El reporte de hoy ya fue enviado.',
          wrong_state: 'Hoy ya no se puede cambiar.',
          date_not_allowed: 'Ese día ya no se puede cambiar.',
          generic: 'No se pudo guardar. Revise su señal e intente de nuevo.',
        },
      },
      photosAdded: 'Enviando {{count}} foto(s)…',
      openReport: 'Abrir el reporte {{no}}',
      pastReports: 'Reportes anteriores',
      failed: 'No se pudo cargar hoy. Revise su señal e intente de nuevo.',
    },
    recording: {
      title: 'Grabando',
      paused: 'En pausa',
      starting: 'Iniciando…',
      keepTalking: 'Siga hablando — la pantalla se queda encendida',
      pausedHint: 'En pausa — toque reproducir para seguir',
      timerLabel: 'Tiempo de grabación',
      pause: 'Pausar la grabación',
      resume: 'Reanudar la grabación',
      stopSend: 'Detener y enviar',
      addPhoto: 'Agregar foto',
      photos: 'Fotos',
      photo: 'Foto {{n}}',
      removePhoto: 'Quitar la foto {{n}}',
      maxReached: '15 minutos — enviando este clip. Grabe otro si lo necesita.',
      tooShort: 'No pude escuchar nada — intente de nuevo',
      permission: {
        title: 'Se necesita el micrófono',
        body: 'Permita el micrófono para que TalkOlog pueda oír su reporte.',
        ios: 'iPhone: Ajustes → Safari → Micrófono → Permitir',
        android: 'Android: toque el candado junto a la dirección → Permisos → Micrófono',
        fallback: 'O grabe con la grabadora de voz de su teléfono y envíe el archivo:',
        pick: 'Elegir una grabación',
        orCall: 'O llame al {{number}}',
      },
      unsupported: {
        title: 'Este navegador no puede grabar aquí',
        body: 'Grabe con la grabadora de voz de su teléfono y envíe el archivo.',
      },
      failed: 'La grabación se detuvo de forma inesperada. Intente de nuevo.',
      interrupted: 'El micrófono se detuvo — una llamada u otra app pudo haberlo tomado.',
    },
    sending: {
      title: 'Enviando…',
      body: 'Enviando su grabación a la oficina.',
      queued: 'Guardado en su teléfono — se enviará cuando tenga señal',
      queuedBody: 'Deje la app abierta o vuelva más tarde; se envía sola.',
      failed: 'No se pudo enviar esta grabación.',
      retry: 'Intentar de nuevo',
      remove: 'Quitar esta grabación',
      backToToday: 'Volver a Hoy',
      errors: {
        report_filed: 'El reporte de hoy ya fue enviado, así que esto no se puede agregar.',
        day_closed: 'Este día está cerrado para la obra, así que esto no se puede enviar.',
        date_not_allowed: 'Ese día ya no admite reporte.',
        upload_too_large: 'El archivo es demasiado grande (máximo 50 MB).',
        media_type_not_allowed: 'Ese tipo de archivo no se puede enviar.',
        device_unverified: 'Abra su enlace de nuevo para verificar este teléfono; luego se envía.',
        consent_required: 'Abra su enlace de nuevo para terminar la configuración; luego se envía.',
        link_expired:
          'Este enlace venció. Abra el enlace de su último mensaje de texto; luego se envía.',
        link_revoked:
          'Este enlace fue desactivado. Abra el enlace de su último mensaje de texto; luego se envía.',
        link_not_found:
          'Este enlace no funciona. Abra el enlace de su último mensaje de texto; luego se envía.',
        reporter_disabled:
          'Sus reportes están en pausa. Consulte a la oficina y luego intente de nuevo.',
        site_inactive: 'Esta obra está en pausa. Consulte a la oficina y luego intente de nuevo.',
        file_missing: 'El archivo ya no está en este teléfono.',
        photos_window_closed:
          'Este reporte ya no admite fotos: el plazo terminó a las 23:59 del día siguiente.',
        photo_limit:
          'Este reporte ya tiene el máximo de fotos que se pueden agregar después de enviarlo.',
        generic: 'No se pudo enviar. Pida ayuda a la oficina.',
      },
    },
    processing: {
      title: 'Escribiendo su reporte…',
      report: 'Reporte {{no}}',
      close: 'Puede cerrar esto — le enviaré un mensaje',
      counts: '{{voiceNotes}} nota(s) de voz · {{photos}} foto(s)',
      ticks: {
        transcribed: 'Transcrito',
        crew: 'Cuadrilla encontrada',
        weather: 'Clima registrado',
        hours: 'Revisando horas',
      },
      tickDone: 'listo',
      steps: {
        transcribed: 'Transcrito, sin el ruido de la obra',
        crewOne: '{{n}} en la obra, 1 subcontratista',
        crewMany: '{{n}} en la obra, {{k}} subcontratistas',
        weather: '{{condition}} {{start}}–{{end}} registrado como demora',
        hours: 'Revisando las horas de los subcontratistas…',
      },
      readyTitle: 'Su reporte está listo para revisar',
      failed: 'No se pudo consultar su reporte. Revise su señal e intente de nuevo.',
      notFound: 'Este reporte ya no está disponible aquí.',
    },
    question: {
      progress: 'Escribiendo su reporte',
      badge: 'Revisión rápida',
      heardAt: 'Escuchado en {{time}} de su nota',
      close: 'Puede cerrar esto — le enviaré un mensaje cuando el borrador esté listo.',
      sending: 'Enviando su respuesta…',
      workers: 'Trabajadores',
      hoursEach: 'Horas cada uno',
      hoursLost: 'Horas perdidas',
      hoursLegend: '¿Por cuánto tiempo?',
      fullDay: 'Todo el día',
      setHours: 'Otras horas',
      send: 'Enviar respuesta',
      invalidNumber: 'Escriba un número.',
      cannotAnswer: 'Esta todavía no se puede responder aquí. La oficina la revisará.',
      other: 'Otra — dígala',
      otherTitle: 'Diga su respuesta',
      otherHint: 'Hasta 30 segundos. No se graba nada más.',
      record: 'Grabar su respuesta',
      recording: 'Grabando',
      stop: 'Detener y enviar',
      typeIt: 'O escríbala',
      noMic:
        'El micrófono está apagado. Escriba el número o permita el micrófono en los ajustes del teléfono.',
      noMicOnly:
        'El micrófono está apagado. Permítalo en los ajustes del teléfono o elija una respuesta.',
      cancel: 'Cancelar',
      listening: 'Escuchando su respuesta…',
      retry: 'No le entendí. Dígalo otra vez o elija una respuesta.',
      unclear: 'No pude entenderlo, así que lo dejé para que la oficina lo revise.',
      queueFailed: 'No se pudo guardar su respuesta en este teléfono. Intente de nuevo.',
      errors: {
        question_closed: 'Esa pregunta ya fue respondida. Esto es lo más reciente.',
        wrong_state: 'Su reporte cambió. Esto es lo más reciente.',
        answer_not_accepted: 'Esa respuesta no funcionó. Intente de nuevo.',
        offline: 'Sin conexión. Revise su señal e intente de nuevo.',
        generic: 'No se pudo enviar su respuesta. Intente de nuevo.',
        answer_pending: 'Todavía estoy escuchando su respuesta anterior.',
      },
    },
    confirmation: {
      crew: '{{company}}: {{workers}} trabajadores, {{hours}} horas cada uno — {{total}} horas.',
      crewOne: '{{company}}: 1 trabajador, {{hours}} horas.',
      workers: '{{company}}: {{workers}} trabajadores.',
      workersOne: '{{company}}: 1 trabajador.',
      hours: '{{company}}: {{hours}} horas cada uno.',
      companyAdded: '{{company}} agregado para que la oficina lo confirme.',
      companyMapped: 'Registrado como {{company}}.',
      weather: 'Clima: {{hours}} horas perdidas.',
      weatherNone: 'Clima: sin tiempo perdido.',
      noted: 'Entendido.',
    },
    draft: {
      eyebrow: 'Borrador · {{date}}',
      title: 'Revise el reporte de hoy',
      crew: 'Cuadrilla',
      answered: 'Respondido',
      crewValue: '{{workers}} trabajadores, {{hours}} horas cada uno',
      crewValueOne: '1 trabajador, {{hours}} horas',
      notStated: 'No indicado',
      noCrew: 'No se indicó cuadrilla',
      weather: 'Clima',
      done: 'Hecho',
      delivered: 'Entregas',
      safety: 'Seguridad',
      next: 'Próximo día',
      photos: 'Fotos',
      photosCount: '{{count}} adjunta(s)',
      none: 'Ninguna',
      fullDayLost: 'día completo perdido',
      status: {
        complete: 'terminado',
        in_progress: 'en curso',
        started: 'iniciado',
        stopped: 'detenido',
        planned: 'planeado',
      },
      percent: '~{{percent}}%',
      toolboxTalk: 'Charla de seguridad',
      noIncidents: 'sin incidentes',
      incidents: '{{count}} incidente(s)',
      confirm: 'Confirmar y enviar',
      confirming: 'Enviando…',
      errors: {
        draft_changed: 'Su reporte cambió. Revíselo de nuevo y envíelo.',
        wrong_state: 'Su reporte cambió. Esto es lo más reciente.',
        offline: 'Sin conexión. Revise su señal e intente de nuevo.',
        generic: 'No se pudo enviar su reporte. Intente de nuevo.',
      },
      fix: {
        button: 'Corregir por voz',
        title: 'Corregir por voz',
        hint: 'Diga qué cambiar, por ejemplo “Los de Apex eran siete, no seis.” Hasta 30 segundos.',
        record: 'Grabar su corrección',
        recording: 'Grabando',
        stop: 'Detener y enviar',
        typeIt: 'O escríbalo',
        typeLabel: '¿Qué hay que cambiar?',
        send: 'Enviar',
        cancel: 'Cancelar',
        noMic:
          'El micrófono está apagado. Permítalo en los ajustes del teléfono, o escriba el cambio.',
        listening: 'Escuchando su corrección…',
        previewTitle: 'Revise el cambio',
        save: 'Guardar cambio',
        saving: 'Guardando…',
        tryAgain: 'Intentar de nuevo',
        discard: 'Dejarlo como estaba',
        unclear: 'No pude hacer ese cambio.',
        unclearHint: 'Dígalo de nuevo con la empresa y el número, o toque los números.',
        stale: 'Su reporte cambió mientras escuchaba. Revíselo y diga la corrección de nuevo.',
        saved: 'Cambio guardado. Revise el reporte de nuevo y envíelo.',
        lines: {
          workers: '{{from}} → {{to}} trabajadores',
          hoursEach: '{{from}} → {{to}} h cada uno',
          total: '{{from}} → {{to}} h',
          added: 'Se agregó {{company}}: {{value}}',
          removed: 'Se quitó {{company}}',
          lostHours: 'Clima: {{from}} → {{to}} h perdidas',
          section: 'Cambió: {{section}}',
        },
        sections: {
          crew: 'Cuadrilla',
          summary: 'Resumen',
          workingHours: 'Horario',
          weather: 'Clima',
          work: 'Hecho',
          deliveries: 'Entregas',
          equipment: 'Equipo',
          safety: 'Seguridad',
          issues: 'Problemas',
          next: 'Próximo día',
        },
        errors: {
          queue: 'No se pudo guardar su grabación en este teléfono. Intente de nuevo.',
          generic: 'No se pudo enviar su corrección. Intente de nuevo.',
          offline: 'Sin conexión. Revise su señal e intente de nuevo.',
          timeout: 'Está tardando demasiado. Intente de nuevo, o toque los números.',
          correction_pending: 'Sigo con su última corrección. Espere un momento.',
        },
      },
      stepper: {
        change: '{{company}}, {{value}}: cambiar',
        hint: 'Cambie los números y guarde.',
        workers: 'Trabajadores',
        hours: 'Horas cada uno',
        fewerWorkers: 'Un trabajador menos',
        moreWorkers: 'Un trabajador más',
        fewerHours: 'Media hora menos',
        moreHours: 'Media hora más',
        total: 'Total: {{total}}',
        save: 'Guardar',
        saving: 'Guardando…',
        cancel: 'Cancelar',
        invalid: 'Escriba un número entero de trabajadores y hasta 24 horas.',
        error: 'No se pudo guardar el cambio. Intente de nuevo.',
      },
    },
    filing: {
      title: 'Archivando su reporte…',
      body: 'Recibirá un mensaje de texto con el PDF en un minuto.',
    },
    filed: {
      title: 'Registrado.',
      body: 'El reporte diario {{no}} ya está en la oficina. Gracias, {{name}}, eso es todo por hoy.',
      bodyWeather: 'El reporte de día de clima {{no}} ya está en la oficina. Gracias, {{name}}.',
      bodyNoWork: 'El reporte de día sin trabajo {{no}} ya está en la oficina. Gracias, {{name}}.',
      pdf: 'Abrir el PDF',
      pdfBadge: 'PDF',
      pdfPreparing: 'Preparando el PDF…',
      pdfLate: 'El PDF está en camino. La oficina ya tiene su reporte.',
      opensNewTab: '(se abre en una pestaña nueva)',
      addPhotos: 'Agregar más fotos',
      weatherPhoto: 'Agregar una foto de las condiciones',
      weatherPhotoHint: 'Una foto de las condiciones refuerza el registro del retraso.',
      pastReports: 'Reportes anteriores',
      hint: '¿Necesita un cambio más tarde? Abra {{pastReports}} y diga qué corregir. La oficina recibe una versión nueva; la anterior se conserva.',
      photos: {
        title: 'Agregar fotos a {{no}}',
        chosen: 'Fotos elegidas: {{count}}',
        photo: 'Foto {{n}}',
        window: 'Fotos que aún puede agregar: {{count}}, hasta {{until}}.',
        tooMany: 'Solo se pueden agregar {{count}} más, así que se guardan las primeras {{count}}.',
        caption: 'Nota de voz (opcional)',
        captionHint: 'Diga qué muestran las fotos. La oficina lo lee debajo de cada foto.',
        record: 'Grabar una nota',
        stop: 'Detener',
        recording: 'Grabando',
        captionReady: 'Nota grabada',
        removeCaption: 'Quitar la nota',
        noMic: 'El micrófono no está disponible. Las fotos se envían sin nota.',
        send: 'Enviar fotos',
        cancel: 'Cancelar',
        adding: 'Agregando sus fotos…',
        captioning: 'Agregando sus fotos y su nota…',
        late: 'Sus fotos están en camino. La oficina las recibirá con su nota.',
        added: 'Fotos agregadas. La versión {{version}} ya está en la oficina.',
        failed: 'No se pudieron agregar las fotos.',
      },
    },
    talk: {
      title: 'Hable ahora',
      assistant: 'Hablando con el asistente de TalkOlog',
      status: {
        microphone: 'Pidiendo el micrófono…',
        connecting: 'Conectando con el asistente…',
        reconnecting: 'Se perdió la conexión — reconectando…',
        speaking: 'El asistente está hablando',
        listening: 'El asistente le escucha',
        muted: 'Su micrófono está apagado',
        ended: 'Llamada terminada. Revisando su reporte…',
      },
      timerLabel: 'Tiempo de llamada',
      noticeLabel: 'Esta llamada se graba',
      mute: 'Silenciar el micrófono',
      unmute: 'Encender el micrófono',
      endCall: 'Terminar llamada',
      enableAudio: 'Toque para oír al asistente',
      recordInstead: 'Mejor grabar',
      errors: {
        rate_limited:
          'Ya inició tres llamadas en esta hora. Puede volver a llamar en {{minutes}} min, o grabe su reporte.',
        talk_now_unavailable: 'Hable ahora no está disponible en este momento. Grabe su reporte.',
        voice_unavailable:
          'No se pudo contactar al asistente. Inténtelo de nuevo o grabe su reporte.',
        kind_not_allowed: 'Este enlace es solo para fotos.',
        connect: 'No se pudo conectar la llamada. Revise su señal e inténtelo de nuevo.',
        generic: 'No se pudo iniciar la llamada. Revise su señal e inténtelo de nuevo.',
      },
    },
    photosLink: {
      title: 'Agregar fotos',
      intro: 'Fotos para el reporte {{no}}, {{site}}, {{date}}.',
      pick: 'Tomar o elegir fotos',
      noReport:
        'Todavía no hay un reporte enviado para este día. Podrá agregar fotos cuando se envíe.',
      closed: 'Este enlace ya no permite agregar fotos.',
      failed: 'No se pudo cargar el reporte. Revise su señal e inténtelo de nuevo.',
    },
    offline: {
      banner: 'Sin señal — las grabaciones se guardan en su teléfono',
      queued: 'Pendientes de envío: {{count}}',
      failed: 'Sin enviar: {{count}}',
      review: 'Revisar',
      sheetTitle: 'Sin enviar',
      voiceNote: 'Nota de voz · {{date}}',
      photo: 'Foto · {{date}}',
      remove: 'Quitar',
      unsaved: 'No se guardó en este teléfono: deje TalkOlog abierto hasta que se envíe.',
    },
    history: {
      intro: 'Sus reportes de los últimos 14 días.',
      empty: 'No hay reportes en los últimos 14 días.',
      status: {
        collecting: 'En curso',
        processing: 'En curso',
        awaiting_answer: 'Necesita su respuesta',
        awaiting_confirmation: 'Listo para revisar',
        filed: 'Enviado',
        signed_off: 'Aprobado',
        void: 'Anulado',
      },
      late: 'Tarde',
      eyebrow: 'Reporte · {{date}}',
      version: 'Versión {{version}}',
      continue: 'Continuar este reporte',
      noData: 'Aún no hay nada que mostrar: TalkOlog todavía está escribiendo este reporte.',
      back: 'Volver a Reportes anteriores',
      failed: 'No se pudieron cargar sus reportes anteriores. Revise su señal e intente de nuevo.',
      reportFailed: 'No se pudo cargar este reporte. Revise su señal e intente de nuevo.',
      change: {
        button: 'Cambiar algo',
        title: 'Cambiar algo',
        hint: 'Diga qué cambiar, por ejemplo “Apex tuvo siete, no seis.” Hasta 30 segundos.',
        lastChange: 'Último cambio: {{note}}',
        saved: 'Cambio guardado. La versión {{version}} está con la oficina.',
        stale: 'El reporte cambió mientras lo escuchaba. Revíselo y diga el cambio otra vez.',
        locked: 'Ese reporte está cerrado. Pida a la oficina que lo cambie.',
      },
    },
    expired: {
      title: 'Este enlace venció',
      body: 'Podemos enviar el enlace de hoy por mensaje al número que la oficina tiene de usted.',
      noCode: 'Abra el enlace de su último mensaje de texto o pida a la oficina el enlace de hoy.',
      send: 'Envíenme el enlace de hoy',
      sending: 'Enviando…',
      sent: 'Enviado. Abra el enlace de hoy en el mensaje que le acabamos de enviar.',
      reasons: {
        site_paused: 'Esta obra está en pausa en TalkOlog. Pida a la oficina que la reactive.',
        site_inactive: 'Esta obra ya no está en su lista de TalkOlog. Consulte a la oficina.',
        not_working_day: 'Hoy no es día laborable en esta obra, así que no hay enlace para hoy.',
        no_consent:
          'Desactivó los mensajes, así que no podemos enviarle el enlace. Pídalo a la oficina.',
        opted_out:
          'Nos pidió no enviarle mensajes, así que no podemos enviar el enlace. Consulte a la oficina.',
        no_channel: 'No pudimos enviar un mensaje a su número. Pida a la oficina que lo revise.',
      },
      errors: {
        rate_limited: 'Ya lo pidió varias veces. Intente de nuevo en {{minutes}} min.',
        offline: 'Sin conexión. Revise su señal e intente de nuevo.',
        generic: 'No se pudo enviar el enlace. Intente de nuevo.',
      },
    },
    settings: {
      channelTitle: '¿Dónde le enviamos los mensajes?',
      channelDefault: 'La mejor opción disponible',
      channelDefaultHint: 'TalkOlog elige, en el orden de su empresa',
      channels: {
        sms: 'Mensaje de texto (SMS)',
        whatsapp: 'WhatsApp',
        push: 'Notificaciones en este teléfono',
      },
      consentsTitle: 'Lo que aceptó',
      legalTitle: 'Privacidad y grabación',
      consents: {
        recording: 'Grabación de mis notas de voz, fotos y llamadas',
        data_processing: 'Procesamiento de mis reportes para {{company}}',
        sms: 'Mensajes de texto con el enlace de mi reporte diario',
        auto_call: 'Llamadas automáticas a la hora del reporte',
        push: 'Notificaciones en este teléfono',
        whatsapp: 'Mensajes de WhatsApp con el enlace de mi reporte diario',
      },
      push: {
        title: 'Notificaciones',
        label: 'Notificaciones en este teléfono',
        hint: 'Los avisos de su reporte le llegan como notificaciones. Si una no llega a este teléfono, le enviamos un mensaje de texto.',
        blocked:
          'Las notificaciones de TalkOlog están bloqueadas en este teléfono. Permítalas en la configuración del teléfono y luego actívelas aquí.',
        failed: 'No se pudieron activar las notificaciones. Intente de nuevo.',
      },
      on: 'Activado',
      off: 'Desactivado',
      turnOff: 'Desactivar',
      turnOffLabel: 'Desactivar: {{consent}}',
      stopSheet: {
        title: '¿Dejar de reportar?',
        body: 'Si lo desactiva, TalkOlog no puede recibir sus notas de voz ni sus fotos, así que sus reportes se detienen. Se avisa a la oficina. Para volver a reportar, acepte de nuevo en este teléfono.',
        confirm: 'Desactivar',
        cancel: 'Dejarlo activado',
      },
      turnOn: 'Activar',
      turnOnLabel: 'Activar: {{consent}}',
      turnOnSheet: {
        title: '¿Volver a activar los mensajes de texto?',
        titleWhatsapp: '¿Activar los mensajes de WhatsApp?',
        titleCall: '¿Activar las llamadas automáticas?',
        body: 'Al activarlos, acepta de nuevo:',
        note: 'Puede desactivarlos aquí cuando quiera.',
        confirm: 'Activar',
        cancel: 'Ahora no',
      },
      textsStopped: {
        start:
          'Detuvo nuestros mensajes al responder STOP. Para recibirlos de nuevo, responda START a uno de nuestros mensajes.',
        office: 'Los mensajes de texto a este teléfono están detenidos. Pregunte a la oficina.',
      },
      notToday: {
        title: 'Llamadas de hoy',
        label: 'No me llamen hoy',
        hint: 'Hoy recibirá los recordatorios como mensajes. Las llamadas vuelven mañana.',
        button: 'No me llamen hoy',
        on: 'Hoy no hay más llamadas',
        onHint: 'Hoy recibirá los recordatorios como mensajes. Las llamadas vuelven mañana.',
      },
      signOut: 'Cerrar sesión en este teléfono',
      signOutSheet: {
        title: '¿Cerrar sesión en este teléfono?',
        body: 'Para reportar de nuevo desde este teléfono, abra su enlace e ingrese el código nuevo que le enviaremos por mensaje.',
        confirm: 'Cerrar sesión',
        cancel: 'Cancelar',
      },
      saving: 'Guardando…',
      saved: 'Guardado',
      back: 'Volver a Hoy',
      failed: 'No se pudo cargar su configuración. Revise su señal e intente de nuevo.',
      saveFailed: 'No se pudo guardar. Revise su señal e intente de nuevo.',
    },
  },
};
